<template>
  <div>
    <popup-hint hint="bundles">
      <template v-slot:breadcrumbs>
        <breadcrumbs home="Bundles"/>
      </template>
      <template v-slot:top>
        Bundles can be used to package together items, services and duty-paid products for sale.
      </template>
      <template v-slot:actions="{close}">
        <v-btn @click="close()" :to="{name:'InventoryBundlesEditNew'}" color="primary" class="ml-4" small>Create</v-btn>
      </template>
    </popup-hint>

    <purtrak-table name="bundles" objectType="bundles" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-expand
                   listEndPoint="bundles2"
                   fields="id,name,active,sku,price,calculatePrice"
                   :listFilters="listFilters"
                   @dblclick="bundle => $router.push({name:'InventoryBundlesTransactions',params:{bundleId:bundle.id}})"
                   @itemExpanded="({item, value}) => expand(item, value)"
                   :item-class="bundle => (bundle.active ? '' : 'grey--text')"
                   title="Bundles"
                   :print-info="{pageSize:'portrait',
                                 subTitle:[filters.status!='1'?(filters.status=='0'?'Inactive':'All'):'']}"
                   download-endpoint="bundlesExportCSV">
      <template v-slot:filter>
        <filter-menu include-reset @reset="filters.status='1';filters.search=''">
          <v-list-item>
            <filter-search :label="[{text:'Name',value:'name'},{text:'SKU',value:'sku'}]" v-model="filters.search" :search-field.sync="filters.searchField" :search-display.sync="searchDisplay"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Status" dense outlined :items="[{value:'2',text:'All'},{divider:true},{value:'1',text:'Active'},{value:'0',text:'Inactive'}]" v-model="filters.status"/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.search" close small class="ml-1" @click:close="filters.search=''">{{searchDisplay}}</v-chip>
          <v-chip v-show="filters.status!='1'" close small class="ml-1" @click:close="filters.status='1'">{{filters.status=='0'?'Inactive':'All'}}</v-chip>
        </template>
      </template>
      <template v-slot:menu v-if="!readOnly">
        <v-list-item :to="{name:'InventoryBundlesEditNew'}"><v-list-item-title>Create bundle</v-list-item-title></v-list-item>
        <template v-if="$store.getters.company.connectedToAccounting">
          <v-divider/>
          <v-list-item @click="sync"><v-list-item-title>Sync bundles with QBO</v-list-item-title></v-list-item>
        </template>
      </template>
      <template v-slot:item.name="{item,printing}">
        <template v-if="printing">{{item.name}}</template>
        <template v-else>
          <v-hover v-if="!readOnly" v-slot="{hover}">
            <a @click.stop="$router.push({name:'InventoryBundlesEdit',params:{bundleId:item.id}})"
               :class="{'text-decoration-underline':hover}">{{item.name}}</a>
          </v-hover>
          <template v-else>{{item.name}}</template>
          <div v-if="!item.active" class="grey--text text-caption">Inactive</div>
        </template>
      </template>
      <template v-slot:item.price="{value}">
        {{value | currency}}
      </template>
      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'InventoryBundlesEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:item.action="{item}">
        <v-menu :nudge-width="150" offset-y :nudge-left="150">
          <template v-slot:activator="{ on }">
            <v-btn small v-on="on" v-if="$vuetify.breakpoint.mobile" outlined>Bundle operations</v-btn>
            <v-btn small icon v-on="on" v-on:click.prevent v-else><v-icon small>mdi-dots-vertical</v-icon></v-btn>
          </template>
          <v-list dense>
            <v-list-item :to="{name:'InventoryBundlesTransactions',params:{bundleId:item.id}}"><v-list-item-title>Transactions</v-list-item-title></v-list-item>
            <v-list-item v-if="company.allowAudit && company.permissions.includes('audit')" :to="{name:'Audit',query:{entityId:item.id}}"><v-list-item-title>Audit history</v-list-item-title></v-list-item>
            <template v-if="!readOnly">
              <v-divider/>
              <v-list-item :to="{name:'InventoryBundlesEdit',params:{bundleId:item.id}}"><v-list-item-title>Edit bundle</v-list-item-title></v-list-item>
              <v-list-item @click="bundleObject=item;showDelete=true"><v-list-item-title>Delete bundle</v-list-item-title></v-list-item>
            </template>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:expanded-item="{ item, headers }">
        <td :colspan="headers.length">
          <v-card class="my-4 text-caption" :class="expansionPanelClass">
          <v-simple-table dense class="my-2 pl-12" :class="expansionPanelClass" style="width:60%">
            <thead>
              <tr>
                <th :class="expansionPanelClass" width="40%">Name</th>
                <th class="py-0 text-right" :class="expansionPanelClass" width="35%">Quantity</th>
                <th class="py-0 text-right" :class="expansionPanelClass" width="25%" v-if="item.calculatePrice">Price</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(line,i) of item.items" :key="i">
                <td class="text-caption">{{line.name}}</td>
                <td class="text-caption py-0 text-right">{{line.quantity}} {{line.units}}</td>
                <td class="text-caption py-0 text-right" v-if="item.calculatePrice">{{line.price | currency}}</td>
              </tr>
            </tbody>
          </v-simple-table>
          </v-card>
        </td>
      </template>
    </purtrak-table>

    <delete-object v-model="showDelete" :name="bundleObject.name" @delete="performDeleteBundle"/>
  </div>
</template>

<script>

export default {
  name: 'InventoryBundles',
  data() {
    return {
      filters: {
        status: '1',
        search: '',
        searchField: 'name'
      },
      searchDisplay: '',
      bundleObject: {},
      showDelete: false,
      defaultColumns: [{ value: 'name', visible:true},
                       { value: 'sku', visible:true},
                       { value: 'price', visible:true}],
      headers: [{ text: 'BUNDLE', value: 'name', width:'60%',sortable:true},
                { text: 'SKU', value: 'sku', width:'20%',sortable:true},
                { text: 'PRICE', value: 'price', width:'20%',sortable:true}]
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    readOnly() {
      return !this.company.permissions.includes('inventory.write');
    },
    listFilters() {
      const filters = [];
      if (this.filters.status == '1' || this.filters.status == '0')
        filters.push({field:'active', comparator:'eq', value:this.filters.status == '1' ? 'true' : 'false'});
      if (this.filters.search)
        filters.push({field:this.filters.searchField, comparator:'contains', value:this.filters.search});
      return filters;
    }
  },
  async created() {
    this.$store.dispatch('checkTasks');
  },
  methods: {
    async expand(item, value) {
      if (value && !item.items) {
        this.loading = true;
        const results = await this.$apiCall(`query InventoryBundles($id:ID!) {
                                                  bundle(id:$id) {
                                                    items{name,quantity,units,price},
                                                  }
                                                }`,
                                            {id:item.id});
        this.$set(item, 'items', results.data.bundle.items);
        this.loading = false;
      }
    },
    async performDeleteBundle() {
      this.loading = true;
      const results = await this.$apiCall(`mutation InventoryBundles($id: ID!) {
                                             objectDelete(id:$id)
                                            }`,
                                          {id:this.bundleObject.id});
      this.loading = false;
      if (!results.error) {
        this.showDelete = false;
        this.$refs.table?.buildList();
        this.$store.commit('setError', {type:'success',message:`Deleted bundle ${this.bundleObject.name}`});
      }
    },
    async sync() {
      this.loading = true;
      const results = await this.$apiCall(`mutation InventoryBundles {
                                             accountingIntegrationSync(step:"bundles") {name}
                                           }`, {});
      this.$refs.table?.buildList();
      if (!results.error)
        this.$store.commit('setError', {type:'success',message:`Synced bundles with QBO`});
    }
  }
}
</script>